import { Column } from "primereact/column";
import { DataTable, DataTableFilterMeta } from "primereact/datatable";
import React, { ChangeEvent, Component } from "react";
import { PageLayout } from "../../../components/ui";
import { Button } from "primereact/button";
import ExtendedInputText from "../../../components/ExtendedInputText";
import { toastService } from "../../../services/toastService";
import { utils } from "../../../utils/utils";
import { fileService } from "../../../services/FileService";
import { ExtendedImage } from "../../../components/Image/ExtendedImage";
import _ from "lodash";
import { SortOrderType } from "../../../models/SearchRequestModel";
import { merchantVendorApiService } from "../../../services/api/merchants/MerchantVendorApiService";
import { UpdateMerchantVendorRequestModel, MerchantVendorResponseModel } from "../../../models/MerchantVendor";
import VendorSettingDialog from "./VendorSettingDialog";
import { paginatorTemplate } from "../../Paginator";
import { FilterMatchMode } from "primereact/api";
import { InputTextFilerTemplate } from "../../Operations/TransactionFilterTemplates";
import { isSuperAdmin } from "../../../authGuard";
import { File, FilterX, Image as ImageIcon, Settings } from "lucide-react";

interface VendorsProps {
    merchantId: number;
}

interface VendorsState {
    isLoading: boolean;
    isSettingDialog: boolean;
    vendors: MerchantVendorResponseModel[];
    imageSourcesVendors: Map<string, string>;
    selectedVendorId: number;
    editable: boolean;
    totalRecords: number;
    rows: number;
    editRowId: number | null;
    showFile: any;
    backupRowData: MerchantVendorResponseModel | null;
    filters: DataTableFilterMeta | undefined;
}

export default class Vendors extends Component<VendorsProps, VendorsState> {
    imageInput: HTMLInputElement | null = null;
    initializeFilters = {
        code: { value: null, matchMode: FilterMatchMode.CONTAINS },
        name: { value: null, matchMode: FilterMatchMode.CONTAINS },
        category: { value: null, matchMode: FilterMatchMode.CONTAINS },
        nameBG: { value: null, matchMode: FilterMatchMode.CONTAINS },
        nameEN: { value: null, matchMode: FilterMatchMode.CONTAINS },
    };

    constructor(props: VendorsProps | Readonly<VendorsProps>) {
        super(props);

        this.state = {
            isLoading: true,
            isSettingDialog: false,
            vendors: [],
            imageSourcesVendors: new Map(),
            selectedVendorId: 0,
            editable: false,
            totalRecords: 0,
            rows: 10,
            editRowId: null,
            showFile: null,
            backupRowData: null,
            filters: this.initializeFilters,
        };
    }

    async componentDidMount() {
        await this.loadVendorsData();
    }

    loadVendorsData = async () => {
        this.setState({ isLoading: true });
        await this.loadVendors();
    };

    loadVendors = async () => {
        const vendors = isSuperAdmin() ? await merchantVendorApiService.getVendors(this.props.merchantId) : await merchantVendorApiService.getOwnVendors();
        if (!vendors.success) {
            toastService.error(vendors.message);
            this.setState({ isLoading: false });
            return;
        }

        this.setState(
            { vendors: vendors.data, totalRecords: vendors.data.length, isLoading: false },
            () => {
                this.preLoadVendorsImages();
            }
        );
    };

    preLoadVendorsImages = async () => {
        const { vendors, imageSourcesVendors } = this.state;
        const updatedImageSources = new Map(imageSourcesVendors);

        vendors.forEach((vendor) => {
            if (vendor.logoFile && !updatedImageSources.has(vendor.logoFile)) {
                this.loadFile(vendor.logoFile)
                    .then((src) => {
                        updatedImageSources.set(vendor.logoFile!, src);
                        this.setState({ imageSourcesVendors: new Map(updatedImageSources) });
                    })
                    .catch(() => {
                        console.error(`Failed to load image for ${vendor.logoFile}`);
                    });
            }
        });
    };

    saveRow = async (newData: MerchantVendorResponseModel) => {
        this.setState({ isLoading: true });
        const request: UpdateMerchantVendorRequestModel = {
            id: newData.id,
            nameBG: newData.nameBG!,
            nameEN: newData.nameEN!,
            logoFile:
                newData.logoFile && newData.logoFile.includes(".")
                    ? utils.removeBase64Data(this.state.imageSourcesVendors.get(newData.logoFile) ?? "")
                    : newData.logoFile!,
        };
        const result = await merchantVendorApiService.updateVendor(request);
        if (!result.success) {
            toastService.error(result.message);
            this.setState({ isLoading: false });
            return;
        }
        toastService.success(result.message);
        this.setState({ editRowId: null, backupRowData: null, showFile: null });
        await this.loadVendorsData();
    };

    editRow = (rowData: MerchantVendorResponseModel) => {
        this.setState({
            editRowId: rowData.id,
            backupRowData: { ...rowData },
        });
    };

    cancelEdit = () => {
        const { editRowId, backupRowData, vendors } = this.state;

        if (editRowId && backupRowData) {
            this.setState({
                vendors: vendors.map((vendor) => (vendor.id === editRowId ? backupRowData : vendor)),
                editRowId: null,
                backupRowData: null,
                showFile: null,
            });
        }
    };

    renderEditableField = (field: keyof MerchantVendorResponseModel, rowData: MerchantVendorResponseModel) => {
        const value = rowData[field] as string;
        return this.state.editRowId === rowData.id ? (
            <ExtendedInputText type="text" name={field} value={value || ""} onChange={(value) => this.onInputChange(field, value, rowData)} />
        ) : (
            rowData[field]
        );
    };
    onInputChange = (fieldName: string, value: string, rowData: MerchantVendorResponseModel) => {
        const updatedVendorServices = this.state.vendors.map((vendor) => {
            if (vendor.id === rowData.id) {
                return { ...vendor, [fieldName]: value };
            }
            return vendor;
        });
        this.setState({ vendors: updatedVendorServices });
    };

    onImageSelected = async (event: ChangeEvent<HTMLInputElement>, editorCallback: (val: any) => void) => {
        const documentFile = event.target.files && event.target.files[0];

        if (!documentFile) return;

        const allowedTypes = ["image/jpeg", "image/png", "image/svg+xml"];
        if (!allowedTypes.includes(documentFile.type)) {
            toastService.error("Only JPEG, PNG, and SVG files are allowed.", "File Type");
            if (this.imageInput) this.imageInput.value = "";
            return;
        } else if (documentFile.size > 1024 * 1024) {
            toastService.error("File size exceeds 1MB.", "File Size");
            if (this.imageInput) this.imageInput.value = "";
            return;
        } else if (!utils.checkCharacterlength(documentFile.name)) {
            toastService.error("File Name Exceeds 128 Characters.", "File Name");
            if (this.imageInput) this.imageInput.value = "";
            return;
        }

        const base64 = await utils.convertFileToBase64(documentFile);
        editorCallback(base64);
        this.setState({ showFile: utils.appendBase64Data(base64 as string) });
    };

    loadFile = async (fileStorageId: string): Promise<string> => {
        if (fileStorageId != null || fileStorageId != "") {
            const imagePath = await fileService.GetFile(fileStorageId);
            if (!imagePath.success) {
                toastService.error(imagePath.message);
                return "";
            }
            return utils.appendBase64Data(imagePath.data, fileStorageId);
        }
        return "";
    };

    onActionButtonClick = (service: MerchantVendorResponseModel) => {
        this.setState({ isSettingDialog: true, selectedVendorId: service.id, editable: service.isDeleted });
    };

    actionBodyTemplate = (rowData: MerchantVendorResponseModel) => {
        return (
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); this.onActionButtonClick(rowData); }}
                className="p-1.5 rounded-md text-neutral-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:text-primary-400 dark:hover:bg-primary-900/20 transition-colors"
                title="Settings"
            >
                <Settings size={15} />
            </button>
        );
    };

    renderLogoFile = (rowData: MerchantVendorResponseModel) => {
        const imageSrc = this.state.imageSourcesVendors.get(rowData.logoFile!) || undefined;
        if (this.state.editRowId === rowData.id) {
            return (
                <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <Button label="Choose" className="ms-1" size="small" icon={<File size={16} />} onClick={() => this.imageInput?.click()} />
                    <input
                        ref={(el) => (this.imageInput = el)}
                        type="file"
                        style={{ display: "none" }}
                        accept="image/jpeg, image/png, image/svg+xml"
                        onChange={(event) =>
                            this.onImageSelected(event, (base64) => {
                                this.onInputChange("logoFile", base64, rowData);
                            })
                        }
                        required={true}
                    />
                    {imageSrc || this.state.showFile ? (
                        <img
                            src={imageSrc || this.state.showFile}
                            width={imageSrc ? "40px" : "30px"}
                            style={{ marginLeft: "10px" }}
                        />
                    ) : (
                        <div className="w-[30px] h-[30px] ml-2.5 flex items-center justify-center bg-neutral-100 dark:bg-neutral-800 rounded">
                            <ImageIcon size={16} className="text-neutral-400" />
                        </div>
                    )}
                </div>
            );
        }
        return (
            <ExtendedImage
                width={imageSrc === undefined ? "30px" : "40px"}
                onPreview={() => this.loadFile(rowData.logoFile ?? "")}
                alt={rowData.logoFile}
                src={imageSrc || ""}
                preview={rowData.logoFile ? true : undefined}
            />
        );
    };

    renderActionButtons = (rowData: MerchantVendorResponseModel) => {
        if (rowData.isDeleted) {
            return null;
        }

        if (this.state.editRowId === rowData.id) {
            return (
                <>
                    <Button text className="p-2" onClick={() => this.saveRow(rowData)} label="Save" />
                    <Button text className="p-2" onClick={this.cancelEdit} label="Cancel" />
                </>
            );
        }

        return (
            <Button text onClick={() => this.editRow(rowData)} label="Edit" />
        );
    };

    renderHeader = () => (
        <span className="p-input-icon-left" style={{ display: "flex", justifyContent: "space-between" }}>
            <Button
                type="button"
                icon={<FilterX size={16} />}
                label="Clear"
                outlined
                onClick={() => {
                    this.setState({ filters: this.initializeFilters });
                }}
            />
        </span>
    );

    render() {
        return (
            <PageLayout noTitle>
                {this.state.isSettingDialog && this.state.selectedVendorId != 0 && (
                                    <VendorSettingDialog
                                        isVisible={this.state.isSettingDialog}
                                        onHide={() => this.setState({ isSettingDialog: false })}
                                        editable={this.state.editable}
                                        vendorId={this.state.selectedVendorId}
                                    />
                                )}

                                <DataTable
                                    header={this.renderHeader}
                                    editMode="row"
                                    dataKey="id"
                                    sortField="id"
                                    sortOrder={SortOrderType.Ascending}
                                    paginator
                                    paginatorTemplate={paginatorTemplate}
                                    rows={this.state.rows}
                                    totalRecords={this.state.totalRecords}
                                    value={this.state.vendors}
                                    loading={this.state.isLoading}
                                    stripedRows={true}
                                    removableSort
                                    rowClassName={(rowData) => (rowData.isDeleted ? "table-secondary" : "")}
                                    filters={this.state.filters}
                                    onFilter={(e) => this.setState({ filters: e.filters })}
                                    filterDisplay="menu"
                                >
                                    <Column
                                        field="code"
                                        header="Code"
                                        sortable
                                        filter
                                        filterField="code"
                                        filterMatchMode="contains"
                                        showFilterMatchModes={false}
                                        filterElement={InputTextFilerTemplate}
                                    />

                                    <Column
                                        field="name"
                                        header="Name"
                                        sortable
                                        filter
                                        filterField="name"
                                        filterMatchMode="contains"
                                        showFilterMatchModes={false}
                                        filterElement={InputTextFilerTemplate}
                                    />

                                    <Column
                                        field="category"
                                        header="Category"
                                        sortable
                                        filter
                                        filterField="category"
                                        filterMatchMode="contains"
                                        showFilterMatchModes={false}
                                        filterElement={InputTextFilerTemplate}
                                    />

                                    <Column
                                        field="nameBG"
                                        header="Name BG"
                                        body={(rowData) => this.renderEditableField("nameBG", rowData)}
                                        sortable
                                        filter
                                        filterField="nameBG"
                                        filterMatchMode="contains"
                                        showFilterMatchModes={false}
                                        filterElement={InputTextFilerTemplate}
                                    />
                                    <Column
                                        field="nameEN"
                                        header="Name EN"
                                        body={(rowData) => this.renderEditableField("nameEN", rowData)}
                                        sortable
                                        filter
                                        filterField="nameEN"
                                        filterMatchMode="contains"
                                        showFilterMatchModes={false}
                                        filterElement={InputTextFilerTemplate}
                                    />

                                    <Column field="logoFile" header="Logo File" body={this.renderLogoFile} />

                                    <Column header="Actions" body={(rowData) => this.renderActionButtons(rowData)} />

                    <Column body={this.actionBodyTemplate} />
                </DataTable>
            </PageLayout>
        );
    }
}
